import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { announcementService } from 'services/api';
import { ComunicadosList } from './ComunicadosPanel';
import styles from './NotificationBell.module.css';

const POLL_MS = 60000;

// Campana de notificaciones del propietario — muestra los comunicados SIN LEER.
// Al abrir uno se marca como leído (persistente); baja el contador y deja de
// aparecer aquí. El historial completo vive en "Mis unidades".
export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [open,  setOpen]  = useState(false);
  const [items, setItems] = useState(null);
  const ref = useRef(null);
  const location = useLocation();

  const refreshCount = useCallback(() => {
    announcementService.unreadCount()
      .then(r => setCount(r.data?.count || 0))
      .catch(() => {});
  }, []);

  useEffect(() => { refreshCount(); }, [refreshCount, location.pathname]);
  useEffect(() => {
    // Con la pestaña oculta nadie ve el número: no se pregunta. Al volver a
    // verla se actualiza de inmediato (visibilitychange), sin esperar el minuto.
    const sondeo = () => { if (document.visibilityState === 'visible') refreshCount(); };
    const t = setInterval(sondeo, POLL_MS);
    document.addEventListener('visibilitychange', sondeo);
    window.addEventListener('announcements:read', refreshCount);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', sondeo);
      window.removeEventListener('announcements:read', refreshCount);
    };
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const abrirPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next) {
      setItems(null);
      try { const r = await announcementService.getAll(); setItems(r.data?.announcements || []); }
      catch { setItems([]); }
    }
  };

  const marcarTodo = async () => {
    try {
      await announcementService.markAllRead();
      setItems(list => (list || []).map(x => ({ ...x, read: true })));
      setCount(0);
      window.dispatchEvent(new Event('announcements:read'));
    } catch { /* noop */ }
  };

  return (
    <div ref={ref} className={styles.caja}>
      <button
        onClick={abrirPanel}
        aria-label={`Notificaciones${count ? ` (${count} sin leer)` : ''}`}
        className={styles.campana}>
        <i className={`fas fa-bell ${count ? styles.campanaIconoConAvisos : styles.campanaIcono}`} />
        {count > 0 && (
          <span className={styles.contador}>{count > 9 ? '9+' : count}</span>
        )}
      </button>

      {open && (
        <div className={styles.panel}>
          <div className={styles.panelCabecera}>
            <span className={styles.panelTitulo}>Comunicados sin leer</span>
            {items && items.some(i => !i.read) && (
              <button onClick={marcarTodo} className={styles.marcarTodo}>
                Marcar todo como leído
              </button>
            )}
          </div>

          <div className={items && items.length ? styles.listaConItems : styles.lista}>
            <ComunicadosList
              items={items}
              onlyUnread
              onChange={() => setCount(c => Math.max(0, c - 1))}
              emptyText="Estás al día — no tienes comunicados sin leer." />
          </div>

          <Link
            to="/admin/unidades"
            onClick={() => setOpen(false)}
            className={styles.historial}>
            Ver historial de comunicados →
          </Link>
        </div>
      )}
    </div>
  );
}
